import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Edit3, 
  Save, 
  X, 
  Shield, 
  Calendar, 
  User, 
  FileText, 
  Tag, 
  Hash,
  ExternalLink,
  Clock,
  HardDrive,
  Monitor
} from 'lucide-react';
import { useMediaRecord } from '../../hooks/useMediaRecord';
import { MediaRecord, MediaRecordUpdateForm, DRMStatus, MediaStatus } from '../../types/mediaRecord';

interface RecordDetailsProps {
  mediaId: string;
  className?: string;
}

const getDRMStatusColor = (status: DRMStatus) => {
  switch (status) {
    case 'protected': return 'text-green-400 bg-green-400/10';
    case 'pending': return 'text-yellow-400 bg-yellow-400/10';
    case 'none': return 'text-gray-400 bg-gray-400/10';
    default: return 'text-gray-400 bg-gray-400/10';
  }
};

const getMediaStatusColor = (status: MediaStatus) => {
  switch (status) {
    case 'active': return 'text-green-400 bg-green-400/10';
    case 'processing': return 'text-blue-400 bg-blue-400/10';
    case 'inactive': return 'text-gray-400 bg-gray-400/10';
    case 'archived': return 'text-orange-400 bg-orange-400/10';
    default: return 'text-gray-400 bg-gray-400/10';
  }
};

const formatFileSize = (bytes: number): string => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

const formatDuration = (milliseconds?: number): string => {
  if (!milliseconds) return 'N/A';
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}:${(minutes % 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
  }
  return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
};

export const RecordDetails: React.FC<RecordDetailsProps> = ({ mediaId, className }) => {
  const { data: record, isLoading, error, update, isUpdating } = useMediaRecord(mediaId);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<MediaRecordUpdateForm>({});

  if (isLoading) {
    return (
      <div className={`cosmic-glass rounded-lg p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-white/10 rounded w-3/4"></div>
          <div className="h-4 bg-white/10 rounded w-full"></div>
          <div className="h-4 bg-white/10 rounded w-2/3"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-20 bg-white/10 rounded"></div>
            <div className="h-20 bg-white/10 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className={`cosmic-glass rounded-lg p-6 ${className}`}>
        <div className="text-center text-red-400">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">Failed to load media record</p>
          <p className="text-sm opacity-75">{error?.message || 'Unknown error occurred'}</p>
        </div>
      </div>
    );
  }

  const handleEdit = () => {
    setEditForm({
      title: record.title,
      description: record.description || '',
      tags: record.tags,
      categories: record.categories,
      metadata: record.metadata
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await update(editForm);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update record:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleTagAdd = (tag: string) => {
    if (tag.trim() && !editForm.tags?.includes(tag.trim())) {
      setEditForm(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag.trim()]
      }));
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    setEditForm(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`cosmic-glass rounded-lg overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {isEditing ? (
              <input
                type="text"
                value={editForm.title || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                className="text-2xl font-bold bg-transparent border-b border-white/20 outline-none w-full pb-2"
                placeholder="Enter title..."
              />
            ) : (
              <h1 className="text-2xl font-bold text-white mb-2">{record.title}</h1>
            )}

            {/* Status badges */}
            <div className="flex items-center gap-2 mb-3">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDRMStatusColor(record.drm_status)}`}>
                <Shield className="w-3 h-3 inline mr-1" />
                DRM: {record.drm_status}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMediaStatusColor(record.status)}`}>
                {record.status}
              </span>
              <span className="px-2 py-1 rounded-full text-xs font-medium text-purple-400 bg-purple-400/10">
                {record.media_type}
              </span>
            </div>

            {isEditing ? (
              <textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full bg-white/5 border border-white/20 rounded p-3 resize-none outline-none"
                rows={3}
                placeholder="Enter description..."
              />
            ) : (
              <p className="text-gray-300 leading-relaxed">
                {record.description || 'No description available'}
              </p>
            )}
          </div>

          <div className="ml-4">
            {isEditing ? (
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={isUpdating}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {isUpdating ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={handleEdit}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Edit3 className="w-4 h-4" />
                Edit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Technical Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white mb-3">Technical Details</h3>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <HardDrive className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-300">Size:</span>
              <span className="text-sm font-medium text-white">{formatFileSize(record.file_size)}</span>
            </div>

            <div className="flex items-center gap-3">
              <FileText className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-300">Format:</span>
              <span className="text-sm font-medium text-white">{record.format}</span>
            </div>

            {record.duration && (
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300">Duration:</span>
                <span className="text-sm font-medium text-white">{formatDuration(record.duration)}</span>
              </div>
            )}

            {record.resolution && (
              <div className="flex items-center gap-3">
                <Monitor className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300">Resolution:</span>
                <span className="text-sm font-medium text-white">
                  {record.resolution.width} × {record.resolution.height}
                </span>
              </div>
            )}

            {record.content_hash && (
              <div className="flex items-center gap-3">
                <Hash className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300">Content Hash:</span>
                <span className="text-xs font-mono text-white bg-white/10 px-2 py-1 rounded">
                  {record.content_hash.substring(0, 16)}...
                </span>
              </div>
            )}

            {record.blockchain_tx_hash && (
              <div className="flex items-center gap-3">
                <ExternalLink className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300">Blockchain:</span>
                <a
                  href={`https://etherscan.io/tx/${record.blockchain_tx_hash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-mono text-blue-400 hover:text-blue-300 bg-white/10 px-2 py-1 rounded transition-colors"
                >
                  {record.blockchain_tx_hash.substring(0, 16)}...
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Metadata */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white mb-3">Metadata</h3>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-300">Created by:</span>
              <span className="text-sm font-medium text-white">{record.created_by.name}</span>
            </div>

            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-300">Created:</span>
              <span className="text-sm font-medium text-white">
                {new Date(record.created_at).toLocaleDateString()}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-300">Updated:</span>
              <span className="text-sm font-medium text-white">
                {new Date(record.updated_at).toLocaleDateString()}
              </span>
            </div>

            {record.latest_version && (
              <div className="flex items-center gap-3">
                <Hash className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300">Version:</span>
                <span className="text-sm font-medium text-white">{record.latest_version.version}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tags and Categories */}
      <div className="p-6 border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tags */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-4 h-4 text-gray-400" />
              <h4 className="font-medium text-white">Tags</h4>
            </div>
            
            {isEditing ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {editForm.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600/20 text-blue-300 text-xs rounded-full"
                    >
                      {tag}
                      <button
                        onClick={() => handleTagRemove(tag)}
                        className="text-blue-400 hover:text-blue-200"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Add tag and press Enter"
                  className="w-full bg-white/5 border border-white/20 rounded px-3 py-2 text-sm outline-none"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleTagAdd(e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {record.tags.length > 0 ? (
                  record.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-blue-600/20 text-blue-300 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-400 text-sm">No tags</span>
                )}
              </div>
            )}
          </div>

          {/* Categories */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-gray-400" />
              <h4 className="font-medium text-white">Categories</h4>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {record.categories.length > 0 ? (
                record.categories.map((category, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-purple-600/20 text-purple-300 text-xs rounded-full"
                  >
                    {category}
                  </span>
                ))
              ) : (
                <span className="text-gray-400 text-sm">No categories</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Summary */}
      {record.analytics_summary && (
        <div className="p-6 border-t border-white/10">
          <h4 className="font-medium text-white mb-3">Quick Stats</h4>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">{record.analytics_summary.total_views}</div>
              <div className="text-xs text-gray-400">Total Views</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">{record.analytics_summary.total_plays}</div>
              <div className="text-xs text-gray-400">Total Plays</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-400">{record.analytics_summary.unique_viewers}</div>
              <div className="text-xs text-gray-400">Unique Viewers</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-400">
                {Math.round(record.analytics_summary.completion_rate * 100)}%
              </div>
              <div className="text-xs text-gray-400">Completion Rate</div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default RecordDetails;